import React, { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import cognitoConfig from '../../../shared/config/cognitoConfig';
import { useIsMounted } from '../../../shared/hooks';
import { LoadingSpinner, ErrorMessage, Button, Logo } from '@bafkal/ui-shared/components';
import { extractContractError } from '@bafkal/ui-shared/utils';
import { UI_MESSAGES } from '../../../shared/constants';

const Login = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMounted = useIsMounted();

  const hasCognitoConfig =
    cognitoConfig.Auth &&
    cognitoConfig.Auth.userPoolId &&
    cognitoConfig.Auth.userPoolWebClientId &&
    cognitoConfig.Auth.oauth &&
    cognitoConfig.Auth.oauth.domain;

  // Always redirect to Cognito Hosted UI if configured
  useEffect(() => {
    const initiateFederatedSignIn = async () => {
      if (!hasCognitoConfig) {
        if (isMounted.current) {
          setError(UI_MESSAGES.AUTH.COGNITO_NOT_CONFIGURED);
        }
        return;
      }

      try {
        if (isMounted.current) {
          setError(null);
          setLoading(true);
        }

        await Auth.federatedSignIn();
      } catch (err) {
        if (isMounted.current) {
          const contractError = extractContractError(err, 'Failed to open Managed Login. Check Cognito configuration.');
          setError(contractError.message);
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    initiateFederatedSignIn();
  }, [hasCognitoConfig, isMounted]);

  const handleRetry = () => {
    setError(null);
    Auth.federatedSignIn().catch((err) => {
      if (isMounted.current) {
        const contractError = extractContractError(err, 'Failed to open Managed Login.');
        setError(contractError.message);
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo className="h-12 w-12 text-blue-600" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Redirecting to AWS Managed Login
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          You will be redirected to the Cognito Hosted UI. If nothing happens,{' '}
          <Button
            onClick={handleRetry}
            variant="link"
            className="hover:text-blue-700 focus:outline-none"
          >
            click here
          </Button>
          .
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <ErrorMessage message={error} onRetry={hasCognitoConfig ? handleRetry : undefined} />

          {!error && (
            <div className="mt-6">
              <LoadingSpinner size="lg" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;